import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
    Gavel, 
    ShoppingBag, 
    Settings, 
    Shield, 
    LogOut,
    Package,
    Heart,
    HelpCircle,
    Users,
    ExternalLink
} from 'lucide-react';
import { getInitials } from '@/lib/utils';
import { useUserCounts } from '@/contexts/UserCountsContext';

export const UserHoverMenu = ({ user, onLogout, inviteCode, onContactAdmin, onUsefulLinksClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { counts } = useUserCounts();
    const timeoutRef = useRef(null);
    const navigate = useNavigate();

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 100);
    };

    const handleToggle = () => {
        // On click, always open (mouseLeave will close it)
        setIsOpen(true);
    };

    const handleNavigation = (path) => {
        setIsOpen(false);
        window.location.href = path;
    };

    const copyInviteLink = async () => {
        if (inviteCode) {
            const link = `${window.location.origin}/register?ref=${inviteCode}`;
            try {
                await navigator.clipboard.writeText(link);
                toast.success('Link copiat în clipboard!');
            } catch (err) {
                const input = document.createElement('input');
                input.value = link;
                document.body.appendChild(input);
                input.select();
                document.execCommand('copy');
                document.body.removeChild(input);
                toast.success('Link copiat în clipboard!');
            }
        } else {
            toast.error('Se încarcă codul de invitație...');
        }
        setIsOpen(false);
    };

    return (
        <div 
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button 
                className="flex items-center p-1 transition-colors rounded-full hover:bg-gray-200"
                data-testid="user-menu-trigger"
                onClick={handleToggle}
                onMouseEnter={handleMouseEnter}
            >
                <Avatar className="w-9 h-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-white text-sm">
                        {getInitials(user?.name || user?.username)}
                    </AvatarFallback>
                </Avatar>
            </button>
            
            {isOpen && (
                <div className="absolute top-full right-[-20px] mt-1 w-64 z-[100] rounded-xl border border-gray-200 shadow-lg overflow-hidden bg-white/95">
                    {/* Content */}
                    <div className="py-2">
                        <div className="px-3 py-2 border-b bg-gray-50/50">
                            <p className="font-medium">{user?.name || user?.username}</p>
                            <p className="text-sm text-muted-foreground">{user?.email}</p>
                        </div>
                        
                        <button onClick={() => handleNavigation('/dashboard?tab=auctions')} className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="my-auctions-link">
                            <div className="flex items-center">
                                <Gavel className="w-4 h-4 mr-3 text-primary" />
                                Licitațiile mele
                            </div>
                            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{counts.myAuctions}</span>
                        </button>
                        <button onClick={() => handleNavigation('/dashboard?tab=buying')} className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="buying-link">
                            <div className="flex items-center">
                                <ShoppingBag className="w-4 h-4 mr-3 text-primary" />
                                Cumpărate
                            </div>
                            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{counts.buying}</span>
                        </button>
                        <button onClick={() => handleNavigation('/dashboard?tab=sold')} className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="sold-link">
                            <div className="flex items-center">
                                <Package className="w-4 h-4 mr-3 text-primary" />
                                Vândute
                            </div>
                            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{counts.sold}</span>
                        </button>
                        <button onClick={() => handleNavigation('/dashboard?tab=for-sale')} className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="for-sale-link">
                            <div className="flex items-center">
                                <Package className="w-4 h-4 mr-3 text-primary" />
                                De vânzare
                            </div>
                            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{counts.forSale}</span>
                        </button>
                        <button onClick={() => handleNavigation('/dashboard?tab=favorites')} className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="favorites-link">
                            <div className="flex items-center">
                                <Heart className="w-4 h-4 mr-3 text-primary" />
                                Apreciate
                            </div>
                            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{counts.favorites}</span>
                        </button>
                        <button onClick={() => handleNavigation('/dashboard?tab=following')} className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="following-link">
                            <div className="flex items-center">
                                <Users className="w-4 h-4 mr-3 text-primary" />
                                Urmăriți
                            </div>
                            <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{counts.following}</span>
                        </button>
                        
                        <div className="border-t my-1" />
                        
                        <button onClick={() => handleNavigation('/dashboard?tab=settings')} className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="settings-link">
                            <Settings className="w-4 h-4 mr-3 text-primary" />
                            Setări cont
                        </button>
                        
                        {user?.role !== 'admin' && user?.role !== 'moderator' && (
                            <button onClick={() => { setIsOpen(false); onContactAdmin?.(); }} className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="contact-admin-link">
                                <HelpCircle className="w-4 h-4 mr-3 text-primary" />
                                Contactează admin
                            </button>
                        )}
                        
                        {user?.role === 'admin' && (
                            <button onClick={() => handleNavigation('/admin')} className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="admin-link">
                                <Shield className="w-4 h-4 mr-3 text-primary" />
                                Admin Panel
                            </button>
                        )}
                        
                        {user?.role === 'moderator' && (
                            <button onClick={() => handleNavigation('/admin')} className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground bg-transparent hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="moderator-link">
                            <Shield className="w-4 h-4 mr-3 text-primary" />
                            Moderare
                        </button>
                    )}
                    
                    <div className="border-t my-1" />
                    
                    {/* Useful Links - Opens Modal */}
                    <button 
                        onClick={() => { setIsOpen(false); onUsefulLinksClick?.(); }}
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-foreground hover:bg-gray-200 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" 
                        data-testid="useful-links-trigger"
                        >
                            <ExternalLink className="w-4 h-4 mr-3 text-primary" />
                            Linkuri utile
                        </button>
                        
                        <div className="border-t my-1" />
                        
                        <button onClick={onLogout} className="flex items-center px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-100 transition-colors rounded-lg mx-1 w-[calc(100%-8px)]" data-testid="logout-btn">
                            <LogOut className="w-4 h-4 mr-3" />
                            Deconectare
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
